/**
 * Wire-контракт чтения доставки каталога AQSI (C5a) и его проверка на границе.
 * Конверта у этих ответов нет: `ResourceId` приходит объектом `{ value }` (или
 * строкой) — ответ ДРУГОГО ресурса отвергается так же, как у чеков.
 */
import {
  array,
  fail,
  instant,
  instantOrNull,
  integer,
  record,
  resourceIdOf,
  sameResource,
  text,
  textOrNull,
  flag,
} from '../../../shared/uc1b/wire'
import type { DeliveryOperation, DeliveryOperations, DeliveryReconciliation, DeliveryStatus } from '../model/types'

function ownedBy(raw: unknown, resourceId: string, where: string): void {
  if (!sameResource(resourceIdOf(raw, `${where}.resourceId`), resourceId)) fail(`${where}: ответ принадлежит другому ресурсу`)
}

export function decodeDeliveryStatus(raw: unknown, resourceId: string): DeliveryStatus {
  const body = record(raw, 'статус')
  ownedBy(body.resourceId, resourceId, 'статус')
  return {
    total: integer(body.total, 'total'),
    pending: integer(body.pending, 'pending'),
    unknown: integer(body.unknown, 'unknown'),
    failed: integer(body.failed, 'failed'),
    succeeded: integer(body.succeeded, 'succeeded'),
    checkpoint: integer(body.checkpoint, 'checkpoint'),
    pauseReason: textOrNull(body.pauseReason, 'pauseReason'),
    retryAt: instantOrNull(body.retryAt, 'retryAt'),
    bindings: integer(body.bindings, 'bindings'),
    confirmedBindings: integer(body.confirmedBindings, 'confirmedBindings'),
    pendingBindings: integer(body.pendingBindings, 'pendingBindings'),
    driftedBindings: integer(body.driftedBindings, 'driftedBindings'),
  }
}

function operationOf(raw: unknown, index: number): DeliveryOperation {
  const where = `items[${index}]`
  const item = record(raw, where)
  return {
    operationId: text(item.operationId, `${where}.operationId`),
    sequence: integer(item.sequence, `${where}.sequence`),
    ruleId: text(item.ruleId, `${where}.ruleId`),
    runId: text(item.runId, `${where}.runId`),
    sourceResourceId: resourceIdOf(item.sourceResourceId, `${where}.sourceResourceId`),
    sourceRemoteKey: text(item.sourceRemoteKey, `${where}.sourceRemoteKey`),
    sourceRevision: text(item.sourceRevision, `${where}.sourceRevision`),
    remoteKey: textOrNull(item.remoteKey, `${where}.remoteKey`),
    status: text(item.status, `${where}.status`),
    step: text(item.step, `${where}.step`),
    stepState: text(item.stepState, `${where}.stepState`),
    stepSends: integer(item.stepSends, `${where}.stepSends`),
    errorCode: textOrNull(item.errorCode, `${where}.errorCode`),
    errorMessage: textOrNull(item.errorMessage, `${where}.errorMessage`),
    admittedAt: instant(item.admittedAt, `${where}.admittedAt`),
    updatedAt: instant(item.updatedAt, `${where}.updatedAt`),
    nextAttemptAt: instantOrNull(item.nextAttemptAt, `${where}.nextAttemptAt`),
  }
}

export function decodeDeliveryOperations(raw: unknown, resourceId: string): DeliveryOperations {
  const body = record(raw, 'операции')
  ownedBy(body.resourceId, resourceId, 'операции')
  return {
    complete: flag(body.complete, 'complete'),
    items: array(body.items, 'items').map(operationOf),
  }
}

export function decodeDeliveryReconciliation(raw: unknown, resourceId: string): DeliveryReconciliation {
  const body = record(raw, 'сверка')
  ownedBy(body.resourceId, resourceId, 'сверка')
  return {
    checkedAt: instant(body.checkedAt, 'checkedAt'),
    snapshotCompletedAt: instantOrNull(body.snapshotCompletedAt, 'snapshotCompletedAt'),
    bindings: integer(body.bindings, 'bindings'),
    confirmed: integer(body.confirmed, 'confirmed'),
    pending: integer(body.pending, 'pending'),
    missingInCabinet: integer(body.missingInCabinet, 'missingInCabinet'),
    deletedInCabinet: integer(body.deletedInCabinet, 'deletedInCabinet'),
    changedInCabinet: integer(body.changedInCabinet, 'changedInCabinet'),
    unknownScheduledForRead: integer(body.unknownScheduledForRead, 'unknownScheduledForRead'),
  }
}
